// src/components/AddExecutiveModal.js
import React, { useState } from 'react';
import { X, Shield, AlertCircle, Plus, Trash2 } from 'lucide-react';

const RISK_LEVELS = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
const PROTECTION_STATUS = ['Active', 'Monitoring', 'Inactive', 'Suspended'];

const AddExecutiveModal = ({ isOpen, onClose, onExecutiveAdded, companies }) => {
  const [formData, setFormData] = useState({
    companyId: '',
    name: '',
    title: '',
    primary_email: '',
    additional_emails: [],
    photo_url: '',
    department: '',
    location: '',
    phone: '',
    linkedin_url: '',
    twitter_handle: '',
    risk_level: 'Medium',
    protection_status: 'Active',
    notes: ''
  });

  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setFormData({
      companyId: '',
      name: '',
      title: '',
      primary_email: '',
      additional_emails: [],
      photo_url: '',
      department: '',
      location: '',
      phone: '',
      linkedin_url: '',
      twitter_handle: '',
      risk_level: 'Medium',
      protection_status: 'Active',
      notes: ''
    });
    setNewEmail('');
    setError('');
  };

  const handleAddEmail = () => {
    if (!newEmail) return;
    
    // Validate email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(newEmail)) {
      setError('Invalid email format');
      return;
    }

    // Check for duplicates
    if (formData.primary_email === newEmail || formData.additional_emails.includes(newEmail)) {
      setError('This email is already added');
      return;
    }

    setFormData({
      ...formData,
      additional_emails: [...formData.additional_emails, newEmail]
    });
    setNewEmail('');
    setError('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setFormData({
      ...formData,
      additional_emails: formData.additional_emails.filter(email => email !== emailToRemove)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await window.Clerk?.session?.getToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (!formData.companyId || !formData.name) {
        throw new Error('Company and name are required');
      }

      // Validate primary email if provided
      if (formData.primary_email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(formData.primary_email)) {
          throw new Error('Invalid primary email format');
        }
      }

      // Find the company slug from numeric_id
      const selectedCompany = companies.find(c => c.numeric_id === parseInt(formData.companyId));
      if (!selectedCompany) {
        throw new Error('Invalid company selected');
      }

      const response = await fetch(`/api/companies/${selectedCompany.id}/executives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          title: formData.title || null,
          primary_email: formData.primary_email || null,
          additional_emails: formData.additional_emails.length > 0 ? formData.additional_emails : null,
          photo_url: formData.photo_url || null,
          department: formData.department || null,
          location: formData.location || null,
          phone: formData.phone || null,
          linkedin_url: formData.linkedin_url || null,
          twitter_handle: formData.twitter_handle || null,
          risk_level: formData.risk_level,
          protection_status: formData.protection_status,
          notes: formData.notes || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create executive');
      }

      console.log('Executive created:', data);
      
      if (onExecutiveAdded) {
        onExecutiveAdded(data);
      }

      resetForm();
      onClose();
      
      alert(`✅ Executive "${data.name}" created successfully!`);

    } catch (err) {
      console.error('Error creating executive:', err);
      setError(err.message || 'Failed to create executive');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-400" />
                Add Executive for Protection
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Register an executive for threat monitoring
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
              disabled={loading}
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* Company Selection */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Company Assignment
              </h3>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">
                  Company <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                  required
                  disabled={loading}
                >
                  <option value="">Select a company</option>
                  {companies?.map((company) => (
                    <option key={company.numeric_id} value={company.numeric_id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Executive Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Executive Information
              </h3>
              
              <div className="space-y-4">
                
                {/* Name & Title */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., John Mitchell"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Chief Executive Officer"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Department & Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Department
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Executive"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., New York, NY"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="e.g., +1 (555) 123-4567"
                    disabled={loading}
                  />
                </div>

              </div>
            </div>

            {/* Email Addresses */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Email Addresses
              </h3>
              
              <div className="space-y-4">
                
                {/* Primary Email */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Primary Email
                  </label>
                  <input
                    type="email"
                    value={formData.primary_email}
                    onChange={(e) => setFormData({ ...formData, primary_email: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="e.g., john.mitchell@company.com"
                    disabled={loading}
                  />
                </div>

                {/* Additional Emails */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Additional Email Addresses
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddEmail();
                        }
                      }}
                      className="flex-1 px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="Add alternative email..."
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={handleAddEmail}
                      disabled={loading || !newEmail}
                      className="px-4 py-2.5 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-white transition-all flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                  {formData.additional_emails.length > 0 && (
                    <div className="space-y-2">
                      {formData.additional_emails.map((email, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-slate-800/30 rounded-lg">
                          <span className="text-sm text-slate-300">{email}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveEmail(email)}
                            className="p-1 hover:bg-red-500/20 rounded text-red-400 hover:text-red-300 transition-all"
                            disabled={loading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Social Media & Online Presence */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Online Presence
              </h3>
              
              <div className="space-y-4">
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      LinkedIn URL
                    </label>
                    <input
                      type="url"
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="https://linkedin.com/in/..."
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Twitter/X Handle
                    </label>
                    <input
                      type="text"
                      value={formData.twitter_handle}
                      onChange={(e) => setFormData({ ...formData, twitter_handle: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="@username"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Photo URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={formData.photo_url}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="https://..."
                    disabled={loading}
                  />
                </div>

              </div>
            </div>

            {/* Protection Settings */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Protection Settings
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Risk Level
                  </label>
                  <select
                    value={formData.risk_level}
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    disabled={loading}
                  >
                    {RISK_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Protection Status
                  </label>
                  <select
                    value={formData.protection_status}
                    onChange={(e) => setFormData({ ...formData, protection_status: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    disabled={loading}
                  >
                    {PROTECTION_STATUS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Notes
              </label>
              <textarea
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                placeholder="Additional notes about this executive..."
                disabled={loading}
              />
            </div>

          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.companyId || !formData.name}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              'Add Executive'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddExecutiveModal;